
import { Navigate, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/lib/supabase";

type SuperUserGuardProps = {
  children: React.ReactNode;
};

export function SuperUserGuard({ children }: SuperUserGuardProps) {
  const { user, loading } = useAuth();
  const location = useLocation();
  const [isSuperUser, setIsSuperUser] = useState<boolean | null>(null);
  const [checkingStatus, setCheckingStatus] = useState(true);

  useEffect(() => {
    const checkSuperUserStatus = async () => {
      if (!user) {
        setIsSuperUser(false);
        setCheckingStatus(false);
        return;
      }

      // First check if email matches allowed emails
      const allowedEmails = [
        'hallryan97@gmail.com',
        'ryan.hall@gtmpact.com',
        'ryan@gmtpact.com'
      ];
      
      if (user.email && allowedEmails.includes(user.email)) {
        setIsSuperUser(true);
        setCheckingStatus(false);
        return;
      }

      // If email doesn't match, check for superuser flag in profiles
      try {
        // Use any type to bypass TypeScript constraints
        const { data, error } = await (supabase as any)
          .from('user_profiles')
          .select('*')
          .eq('id', user.id)
          .single();

        if (error) {
          console.error('Error checking superuser status:', error);
          setIsSuperUser(false);
        } else {
          setIsSuperUser(!!data?.is_superuser);
        }
      } catch (error) {
        console.error('Error in superuser check:', error);
        setIsSuperUser(false);
      }
      
      setCheckingStatus(false);
    };

    checkSuperUserStatus();
  }, [user]);

  if (loading || checkingStatus) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  // Redirect non-superusers to unauthorized page
  if (!isSuperUser) {
    console.log("Not a superuser, redirecting to unauthorized page");
    return <Navigate to="/unauthorized" state={{ from: location }} replace />;
  }

  return <>{children}</>;
}

export default SuperUserGuard;
